import moment from 'moment';

import { IEmployeePresence, IEmployeesList, PresenceStatus } from '../models/types';

export const PRESENCE_STATUS_LABEL: Record<PresenceStatus, string> = {
  on_plan: 'На плане',
  online: 'В сети',
  offline: 'Не в сети'
};

const PRESENCE_STATUS_VALUES = new Set<string>(Object.keys(PRESENCE_STATUS_LABEL));

function isPresenceStatus(value: string): value is PresenceStatus {
  return PRESENCE_STATUS_VALUES.has(value);
}

export interface MergePresenceOptions {
  /** When true, keep existing presence fields — do not default missing rows to offline. */
  presenceFailed?: boolean;
}

/** Relative for recent (< 24h), absolute otherwise — matches list mockup. */
export function formatPresenceTime(iso?: string | null, now = moment()): string {
  if (!iso) {
    return '—';
  }
  const time = moment(iso);
  if (!time.isValid()) {
    return '—';
  }
  if (now.diff(time, 'hours', true) < 24) {
    return time.from(now);
  }
  return time.format('DD.MM.YYYY HH:mm:ss');
}

export function mergeEmployeesWithPresence(
  employees: IEmployeesList[],
  presenceRows: IEmployeePresence[],
  options: MergePresenceOptions = {}
): IEmployeesList[] {
  const { presenceFailed = false } = options;

  if (presenceFailed) {
    return employees.map(employee => ({ ...employee }));
  }

  const byId = new Map(presenceRows.map(row => [String(row.userId), row]));

  return employees.map(employee => {
    const presence = byId.get(String(employee.id));
    if (!presence) {
      return {
        ...employee,
        presenceStatus: 'offline' as PresenceStatus,
        lastContact: null
      };
    }
    return {
      ...employee,
      presenceStatus: presence.presenceStatus,
      lastContact: presence.lastContact ?? null,
      lastPositionTime: presence.lastPositionTime ?? employee.lastPositionTime ?? null,
      lastPositionType: presence.lastPositionType ?? null,
      hasTag: presence.hasTag,
      deviceId: employee.deviceId ?? presence.tagNumber ?? undefined
    };
  });
}

/** Parse one or many presence statuses from a filter value (`online` or `online,on_plan`). */
export function parsePresenceStatuses(
  presenceStatus?: PresenceStatus | string | null
): PresenceStatus[] {
  if (!presenceStatus || presenceStatus === 'null') {
    return [];
  }
  return String(presenceStatus)
    .split(',')
    .map(value => value.trim())
    .filter(isPresenceStatus);
}

export function hasActivePresenceFilter(
  presenceStatus?: PresenceStatus | string | null
): boolean {
  return parsePresenceStatuses(presenceStatus).length > 0;
}

/** Stable CSV for URL/query and cache keys (order-independent). */
export function serializePresenceStatuses(
  statuses: Array<PresenceStatus | string | null | undefined>
): string {
  const unique = Array.from(
    new Set(
      statuses
        .map(value => (value == null ? '' : String(value).trim()))
        .filter(isPresenceStatus)
    )
  ).sort();
  return unique.join(',');
}

export function normalizePresenceStatusFilter(
  presenceStatus?: PresenceStatus | string | null
): string | undefined {
  const statuses = parsePresenceStatuses(presenceStatus);
  if (!statuses.length) {
    return undefined;
  }
  return serializePresenceStatuses(statuses);
}

export function filterEmployeesByPresenceStatus(
  employees: IEmployeesList[],
  presenceStatus?: PresenceStatus | string | null
): IEmployeesList[] {
  const statuses = parsePresenceStatuses(presenceStatus);
  if (!statuses.length) {
    return employees;
  }
  const allowed = new Set(statuses);
  return employees.filter(
    employee => employee.presenceStatus != null && allowed.has(employee.presenceStatus)
  );
}

/** Merge roster with presence rows, then apply presence status filter (OR for multiple). */
export function buildPresenceFilteredRoster(
  rosterAll: IEmployeesList[],
  presenceRows: IEmployeePresence[],
  presenceStatus?: PresenceStatus | string | null
): IEmployeesList[] {
  const merged = mergeEmployeesWithPresence(rosterAll, presenceRows);
  return filterEmployeesByPresenceStatus(merged, presenceStatus);
}

export function paginateList<T>(
  items: T[],
  pageIndex: number,
  pageSize: number
): {
  page: T[];
  pagesCount: number;
} {
  const total = items.length;
  const pagesCount = total === 0 ? 0 : Math.ceil(total / pageSize);
  const safePage = Math.max(0, Math.min(pageIndex, Math.max(pagesCount - 1, 0)));
  const start = safePage * pageSize;
  return {
    page: items.slice(start, start + pageSize),
    pagesCount
  };
}

/** Compare list filters ignoring pagination. */
export function sameEmployeeFilters(
  a?: Record<string, unknown> | null,
  b?: Record<string, unknown> | null
): boolean {
  if (!a || !b) {
    return false;
  }
  const keys = [
    'presenceStatus',
    'fullTextSearch',
    'departmentId',
    'device',
    'lastPosition',
    'roles',
    'posts',
    'includePreview'
  ];
  return keys.every(key => {
    if (key === 'presenceStatus') {
      return (
        serializePresenceStatuses(parsePresenceStatuses(String(a[key] ?? ''))) ===
        serializePresenceStatuses(parsePresenceStatuses(String(b[key] ?? '')))
      );
    }
    return String(a[key] ?? '') === String(b[key] ?? '');
  });
}
